// REST-клиент agentbox. Граница между shell/ и panes/ — обе стороны
// ходят на сервер только отсюда. Владелец файла — интегратор, не агенты.

import type {
  AuthResponse, CreateSessionRequest, LogPage, MeResponse, PatchSessionRequest, UsageResponse, VoiceItem,
  AttachmentResponse, Comment, CreateNoteRequest, FeedPage, Graph, NoteContext,
  NoteFull, NoteMeta, ProfileInfo, PurgeResponse, SaveNoteRequest, SearchHit,
  SessionInfo, TreeNode, VoiceResponse, YonoteCollection, YonoteExport,
  MessageAction, NoteStatus,
} from './protocol'
import { trackBoot } from './lib/booting'

const TOKEN_KEY = 'agentbox.token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

/** Сколько ждать ответ, прежде чем считать связь потерянной.
 *
 *  Без таймаута зависший запрос вешает весь экран: на мобильной сети ответ
 *  может уйти с сервера (в логах он 200), но не доехать до телефона — и тогда
 *  `fetch` не завершается никогда. Панель показывала «подключение к серверу…»
 *  бесконечно, без единого способа выйти из этого состояния.
 */
const TIMEOUT_MS = 15_000
/** Расшифровка речи считается на сервере и честно бывает долгой. */
const VOICE_TIMEOUT_MS = 180_000
const VOICE_AUTOSEND_TIMEOUT_MS = 900_000
/** Хвост лога большой сессии тоже качается дольше обычного запроса. */
const LOG_TIMEOUT_MS = 45_000
/** Подъём сессии: процесс claude стартует вместе с MCP-серверами, на
 *  нагруженном боксе это дольше 15 с. Сервер досоздаёт сессию, даже если
 *  браузер оборвал запрос, но окно показало бы ошибку — ждём до минуты,
 *  плашка ожидания — lib/booting.ts. */
const SESSION_TIMEOUT_MS = 60_000
const SESSION_TIMEOUT_TEXT =
  'Сессия не поднялась за минуту. Сервер доделает запуск сам — она появится в списке бесед.'

async function request<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs: number = TIMEOUT_MS,
  timeoutText = 'Сервер не ответил вовремя',
): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  // Токен панели идёт в СВОЁМ заголовке, а не в Authorization.
  // Панель может стоять за HTTP Basic на прокси, а Authorization в запросе
  // только один: Bearer затирал бы Basic-учётку браузера, прокси отвечал бы
  // 401, и браузер выбрасывал бы окно с паролем на каждый запрос.
  if (token) headers.set('X-Agentbox-Token', token)
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  const abort = new AbortController()
  const timer = window.setTimeout(() => abort.abort(), timeoutMs)
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers,
      credentials: 'same-origin',
      signal: abort.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(408, timeoutText)
    }
    throw error
  } finally {
    window.clearTimeout(timer)
  }
  if (res.status === 401) {
    setToken(null)
    throw new ApiError(401, 'Сессия оператора истекла')
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new ApiError(res.status, detail || `Ошибка ${res.status}`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  /** Список профилей — единственный запрос, который делается до входа. */
  profiles: () => request<ProfileInfo[]>('/auth/profiles'),
  login: (password: string, profile?: string | null) =>
    request<AuthResponse>('/auth', {
      method: 'POST',
      body: JSON.stringify(profile ? { password, profile } : { password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<MeResponse>('/auth/me'),

  listSessions: () => request<SessionInfo[]>('/sessions'),
  /** Лимиты подписки Claude — «5 часов» и «неделя», как /usage в консоли. */
  usage: () => request<UsageResponse>('/usage'),
  createSession: (body: CreateSessionRequest) => trackBoot(
    body.type === 'shell' ? 'открываю консоль' : 'поднимаю сессию',
    request<SessionInfo>('/sessions', { method: 'POST', body: JSON.stringify(body) },
      SESSION_TIMEOUT_MS, SESSION_TIMEOUT_TEXT),
  ),
  patchSession: (id: string, body: PatchSessionRequest) =>
    request<SessionInfo>(`/sessions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** Остановить процесс: лог остаётся читаемым, сессия уезжает в историю. */
  killSession: (id: string) => request<void>(`/sessions/${id}`, { method: 'DELETE' }),
  /** Удалить насовсем вместе с логом на диске. */
  purgeSession: (id: string) =>
    request<void>(`/sessions/${id}?purge=true`, { method: 'DELETE' }),
  /** Закреплённая сессия-владелец телеграма: создаст, поднимет или вернёт живую. */
  telegramSession: () => trackBoot('поднимаю канал Телеграма',
    request<SessionInfo>('/sessions/telegram', { method: 'POST' }, SESSION_TIMEOUT_MS, SESSION_TIMEOUT_TEXT),
  ),
  /** Стереть всю историю профиля: живые сессии не трогает. */
  purgeArchive: () => request<PurgeResponse>('/sessions/purge', { method: 'POST' }),
  interrupt: (id: string) => request<void>(`/sessions/${id}/interrupt`, { method: 'POST' }),
  /** force=true перезапускает и живую сессию: процесс агента поднимается
   *  заново вместе с MCP-серверами, разговор продолжается тот же. */
  resume: (id: string, force = false) => trackBoot(
    force ? 'перезапускаю агента' : 'поднимаю сессию',
    request<SessionInfo>(`/sessions/${id}/resume${force ? '?force=1' : ''}`, { method: 'POST' },
      SESSION_TIMEOUT_MS, SESSION_TIMEOUT_TEXT),
  ),
  log: (id: string, since = 0, limit?: number) =>
    request<LogPage>(
      `/sessions/${id}/log?since=${since}${limit ? `&limit=${limit}` : ''}`,
      {},
      LOG_TIMEOUT_MS,
    ),

  /** «Запиши»: заметка из выводов агента, реплики оператора в неё не идут. */
  saveNote: (id: string, body: { folder?: string; title?: string } = {}) =>
    request<NoteMeta>(`/sessions/${id}/save-note`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  /** Вложение: файл ложится в каталог сессии, агенту уходит путь к нему. */
  attach: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file, file.name)
    return request<AttachmentResponse>(
      `/sessions/${id}/attachments`,
      { method: 'POST', body: form },
      VOICE_TIMEOUT_MS,
    )
  },

  /** Сохранённые расшифровки голосовых беседы, новые сверху. */
  voiceList: (id: string) => request<VoiceItem[]>(`/sessions/${id}/voice`),
  voiceGet: (id: string, voiceId: string) =>
    request<VoiceItem>(`/sessions/${id}/voice/${encodeURIComponent(voiceId)}`),
  /** Голосовое → текст. `autosend` — сервер сам отправит расшифровку агенту
   *  (кнопка «отправить» в беседе); без него текст возвращается клиенту. */
  voice: (id: string, audio: Blob, opts: { autosend?: boolean } = {}) => {
    const form = new FormData()
    form.append('audio', audio, 'voice.webm')
    return request<VoiceResponse>(
      `/sessions/${id}/voice${opts.autosend ? '?autosend=1' : ''}`,
      { method: 'POST', body: form },
      // при autosend ответ нужен только для индикатора — ждём дольше, чем
      // длится расшифровка самой длинной записи, а работа на сервере от
      // этого таймаута всё равно не зависит
      opts.autosend ? VOICE_AUTOSEND_TIMEOUT_MS : VOICE_TIMEOUT_MS,
    )
  },
}

/** База знаний профиля. Профиль сервер берёт из токена — в путях его нет:
 *  чужая база для профиля просто не существует (HUB_CONTRACT.md). */
export const notesApi = {
  tree: () => request<TreeNode[]>('/notes/tree', {}),

  get: (path: string) => request<NoteFull>(`/notes/note?path=${encodeURIComponent(path)}`, {}),

  create: (body: CreateNoteRequest) =>
    request<NoteFull>('/notes/note', { method: 'POST', body: JSON.stringify(body) }),

  /** Автосохранение: зовётся раз в ~2 с после последней правки. */
  save: (path: string, body: SaveNoteRequest) =>
    request<NoteMeta>(`/notes/note?path=${encodeURIComponent(path)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  move: (path: string, to: string) =>
    request<NoteMeta>('/notes/move', { method: 'POST', body: JSON.stringify({ path, to }) }),

  remove: (path: string) =>
    request<void>(`/notes/note?path=${encodeURIComponent(path)}`, { method: 'DELETE' }),

  folder: (path: string) =>
    request<TreeNode>('/notes/folder', { method: 'POST', body: JSON.stringify({ path }) }),

  search: (q: string, opts: { type?: string; track?: string; limit?: number } = {}) => {
    const params = new URLSearchParams({ q })
    if (opts.type) params.set('type', opts.type)
    if (opts.track) params.set('track', opts.track)
    if (opts.limit) params.set('limit', String(opts.limit))
    return request<SearchHit[]>(`/notes/search?${params.toString()}`, {})
  },

  feed: (limit?: number) =>
    request<FeedPage>(`/notes/feed${limit ? `?limit=${limit}` : ''}`, {}),

  // --- вторая очередь ---

  comments: (path: string) =>
    request<Comment[]>(`/notes/comments?path=${encodeURIComponent(path)}`, {}),

  comment: (path: string, text: string) =>
    request<Comment>(`/notes/comments?path=${encodeURIComponent(path)}`, {
      method: 'POST', body: JSON.stringify({ text }),
    }),

  context: (path: string) =>
    request<NoteContext>(`/notes/context?path=${encodeURIComponent(path)}`, {}),

  saveContext: (path: string, body: Partial<Pick<NoteContext, 'decided' | 'open' | 'sessionId'>>) =>
    request<NoteContext>(`/notes/context?path=${encodeURIComponent(path)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  /** «Отдать агенту»: новая беседа, начатая выжимкой контекста. */
  handoff: (path: string) =>
    request<SessionInfo>(`/notes/context/handoff?path=${encodeURIComponent(path)}`, {
      method: 'POST', body: JSON.stringify({}),
    }),

  graph: (opts: { type?: string; track?: string; from?: string; depth?: number } = {}) => {
    const params = new URLSearchParams()
    if (opts.type) params.set('type', opts.type)
    if (opts.track) params.set('track', opts.track)
    if (opts.from) params.set('from', opts.from)
    if (opts.depth) params.set('depth', String(opts.depth))
    const query = params.toString()
    return request<Graph>(`/notes/graph${query ? `?${query}` : ''}`, {})
  },

  yonoteCollections: () => request<YonoteCollection[]>('/notes/yonote/collections', {}),

  /** Всегда создаёт НОВЫЙ документ: при обновлении Yonote теряет разметку. */
  toYonote: (path: string, collectionId: string) =>
    request<YonoteExport>(`/notes/yonote?path=${encodeURIComponent(path)}`, {
      method: 'POST', body: JSON.stringify({ collectionId }),
    }),

  /** Без `paths` отмечает прочитанным всё. */
  markRead: (paths?: string[]) =>
    request<{ unread: number }>('/notes/feed/read', {
      method: 'POST', body: JSON.stringify(paths ? { paths } : {}),
    }),
}

/** Трекер сообщений и канбан задач (PLAN.md «Трекер сообщений из Telegram»).
 *  Тоже заметки базы знаний — эндпоинты только фильтруют по type и двигают
 *  status/action, см. server/agentbox/api/routes_tracker.py. */
export const trackerApi = {
  messages: (status?: NoteStatus) =>
    request<NoteMeta[]>(`/tracker/messages${status ? `?status=${status}` : ''}`, {}),

  action: (path: string, action: MessageAction) =>
    request<NoteMeta>('/tracker/messages/action', {
      method: 'POST', body: JSON.stringify({ path, action }),
    }),

  /** «Взять в работу»: сообщение становится карточкой канбана. */
  promote: (path: string) =>
    request<NoteMeta>('/tracker/messages/promote', {
      method: 'POST', body: JSON.stringify({ path }),
    }),

  tasks: (status?: NoteStatus) =>
    request<NoteMeta[]>(`/tracker/tasks${status ? `?status=${status}` : ''}`, {}),

  taskStatus: (path: string, status: NoteStatus) =>
    request<NoteMeta>('/tracker/tasks/status', {
      method: 'POST', body: JSON.stringify({ path, status }),
    }),

  // ---- трекер v3 (docs/api-tracker-v3.md); типы — sections/tracker/types.ts ----
  /** Сырой вызов эндпоинтов трекера v3: раздел сам типизирует ответ. */
  v3: <T>(path: string, method: 'GET' | 'POST' | 'PATCH' | 'DELETE' = 'GET', body?: unknown) =>
    request<T>(`/tracker${path}`, {
      method, body: body === undefined ? undefined : JSON.stringify(body),
    }),
}

export function streamUrl(sessionId: string, since: number): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  const token = encodeURIComponent(getToken() ?? '')
  return `${proto}//${location.host}/api/sessions/${sessionId}/stream?token=${token}&since=${since}`
}
